// File: components/ContactFloatingButtons.js
import React, { useState } from 'react';
import { Box, Fab, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Import các Icons từ @material-ui/icons
import PhoneIcon from '@material-ui/icons/Phone';
import ForumIcon from '@material-ui/icons/Forum'; // Biểu tượng cho Messenger
import ChatIcon from '@material-ui/icons/Chat';   // Biểu tượng cho Zalo
import LocationOnIcon from '@material-ui/icons/LocationOn';
import DescriptionIcon from '@material-ui/icons/Description'; // Biểu tượng cho đăng ký tư vấn
import YeuCauBaoGiaModal from '../modal/YeuCauBaoGiaModal';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'fixed',
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column', // Xếp các nút theo cột
        alignItems: 'flex-end',   // Các nút sẽ căn lề phải
        zIndex: 1000,
        [theme.breakpoints.down('sm')]: { // Khi màn hình nhỏ hơn 'sm' (mobile)
            width: '100%',
            bottom: 0,
            right: 'auto',
            left: 0,
            flexDirection: 'row',
            justifyContent: 'space-around',
            padding: theme.spacing(1),
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[6],
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
            alignItems: 'center',
        },
    },
    buttonWrapper: {
        display: 'flex',
        alignItems: 'center', // Căn giữa Fab và Label theo chiều dọc
        marginBottom: theme.spacing(2),
        textDecoration: 'none',
        color: 'inherit',
        position: 'relative', // Quan trọng: Đặt vị trí tương đối cho container
        marginRight: 0, // Đảm bảo không có margin bên phải mặc định
        // Thêm transition cho desktop
        [theme.breakpoints.up('md')]: {
            '&:hover $label': { // Khi hover wrapper, hiển thị label bên trong
                opacity: 1,
                transform: 'translateX(0)', // Trượt vào
            },
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: 0,
            marginRight: theme.spacing(1),
            marginLeft: theme.spacing(1),
            flexDirection: 'column', // Stack icon và label vertically on mobile
        },
    },
    fab: {
        zIndex: 2, // Đảm bảo FAB nằm trên label (trên desktop)
        [theme.breakpoints.down('sm')]: {
            width: theme.spacing(6),
            height: theme.spacing(6),
            marginBottom: theme.spacing(0.5),
        },
    },
    // Các màu Fab tùy chỉnh (giữ nguyên)
    phoneFab: {
        backgroundColor: '#4CAF50',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#388E3C',
        },
    },
    messengerFab: {
        backgroundColor: '#0078FF',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#0056B3',
        },
    },
    zaloFab: {
        backgroundColor: '#0084FF',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#006BB3',
        },
    },
    mapFab: {
        backgroundColor: '#2196F3',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#1976D2',
        },
    },
    registerFab: {
        backgroundColor: '#673AB7',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#512DA8',
        },
    },
    label: {
        // Ẩn label trên desktop mặc định
        opacity: 0, // Ban đầu ẩn đi
        transform: 'translateX(100%)', // Đẩy label ra ngoài bên phải
        transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out', // Hiệu ứng chuyển động
        whiteSpace: 'nowrap', // Ngăn không cho label bị xuống dòng
        // Đặt vị trí tuyệt đối cho label trên desktop
        position: 'absolute',
        right: theme.spacing(8), // Đặt label bên trái của FAB. theme.spacing(7) (kích thước FAB) + theme.spacing(1) (khoảng cách)
        backgroundColor: 'rgba(0, 0, 0, 0.7)', // Nền đen mờ
        color: '#fff',
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.shape.borderRadius,
        pointerEvents: 'none', // Quan trọng: Đảm bảo không bắt sự kiện chuột trên label
        zIndex: 1, // Đảm bảo label nằm dưới FAB một chút
        [theme.breakpoints.down('sm')]: { // Hiển thị label trên mobile
            opacity: 1,
            transform: 'translateX(0)',
            position: 'static', // Đặt lại về static trên mobile
            backgroundColor: 'transparent',
            color: 'inherit',
            padding: 0,
            marginTop: theme.spacing(0.5),
            fontSize: '0.75rem',
            pointerEvents: 'auto',
        },
    },
}));

const ContactFloatingButtons = () => {
    const classes = useStyles();

    const [isModalOpenYeuCauBaoGia, setIsModalOpenYeuCauBaoGia] = useState(false);
    const handleOpenModalYeuCauBaoGia = () => {
        setIsModalOpenYeuCauBaoGia(true);
    };
    const handleCloseModalYeuCauBaoGia = () => {
        setIsModalOpenYeuCauBaoGia(false);
    };
    const buttons = [
        { label: 'Gọi ngay', icon: PhoneIcon, url: 'tel:0966122815', fabClass: classes.phoneFab, type: 'link' },
        { label: 'Messenger', icon: ForumIcon, url: 'https://m.me/61576031008836', fabClass: classes.messengerFab, type: 'link' },
        { label: 'Zalo chat', icon: ChatIcon, url: 'https://zalo.me/0966122815', fabClass: classes.zaloFab, type: 'link' },
        { label: 'Địa chỉ', icon: LocationOnIcon, url: '/gioi-thieu', fabClass: classes.mapFab, type: 'link' },
        { label: 'Đăng ký tư vấn', icon: DescriptionIcon, url: '/thong-tin-lien-he', fabClass: classes.registerFab, type: 'modal', action: handleOpenModalYeuCauBaoGia },
    ];



    return (
        <Box className={classes.root}>
            {buttons.map((button, index) => {
                if (button.type === 'link') {
                    return (
                        <a key={index} href={button.url} target="_blank" rel="noopener noreferrer" className={classes.buttonWrapper}>
                            <Typography variant="caption" className={classes.label}>
                                {button.label}
                            </Typography>
                            <Fab size="medium" className={`${classes.fab} ${button.fabClass}`}>
                                <button.icon />
                            </Fab>
                        </a>
                    );
                } else if (button.type === 'modal') {
                    return (
                        <Box key={index} onClick={button.action} className={`${classes.buttonWrapper} ${classes.modalButtonWrapper}`}>
                            <Typography variant="caption" className={classes.label}>
                                {button.label}
                            </Typography>
                            <Fab size="medium" className={`${classes.fab} ${button.fabClass}`}>
                                <button.icon />
                            </Fab>
                        </Box>
                    );
                }
                return null;
            })}

            <YeuCauBaoGiaModal
                open={isModalOpenYeuCauBaoGia}
                onClose={handleCloseModalYeuCauBaoGia}
            />
        </Box>
    );
};

export default ContactFloatingButtons;